
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { confirmCheckout } from "@/lib/payment";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Loader2, AlertTriangle, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

interface GolfRegistrationConfirmationData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  amount: number;
  cerfa_url: string | null;
}

export default function GolfSuccess() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;
  const processed = useRef(false);

  const getSessionId = useCallback(() => {
    return searchParams.get("session_id") || localStorage.getItem("golf_stripe_session_id");
  }, [searchParams]);

  const sessionId = getSessionId();

  const processPayment = useCallback(async (sid: string) => {
    if (processed.current) return;
    try {
      const result = await confirmCheckout(sid);
      if (result.status === "paid") {
        processed.current = true;
        setSuccess(true);
        localStorage.removeItem("golf_stripe_session_id");
      } else {
        if (retryCount < maxRetries) {
          setTimeout(() => setRetryCount((prev) => prev + 1), (retryCount + 1) * 2000);
          return;
        }
        throw new Error("Le paiement n'a pas pu être confirmé");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [retryCount, maxRetries]);

  useEffect(() => {
    if (sessionId) {
      processPayment(sessionId);
    } else {
      setError("Session ID introuvable.");
      setIsLoading(false);
    }
  }, [sessionId, processPayment, retryCount]);

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
      {/* Background with gradient and gavroche */}
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full bg-gradient-to-br from-[#FFFF4D] via-[#FFE94B] to-[#A3D960]"></div>
        <div className="absolute top-0 right-0 h-full w-1/3 md:w-2/5 opacity-30 md:opacity-40 flex items-end">
          <img 
            src="/lovable-uploads/180f51c0-05cf-4dfd-b4b7-91b153baf486.png" 
            alt="Décoration" 
            className="h-auto w-full object-contain object-bottom drop-shadow-xl" 
          />
        </div>
      </div>
      <div className="max-w-xl w-full mx-4 relative z-10">
        <div className="text-center mb-6">
          <Trophy className="h-10 w-10 text-[#00509E] mx-auto mb-2" />
          <h1 className="text-xl font-bold text-[#001233]">Compétition de Golf 2026 au profit de<br />Enfants De La République</h1>
        </div>
        <Card className="border-0 shadow-2xl">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-10">
                <Loader2 className="h-12 w-12 text-[#00509E] animate-spin mx-auto" />
                <p className="mt-4 text-lg text-foreground">Confirmation du paiement en cours...</p>
                {retryCount > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Tentative {retryCount}/{maxRetries}...
                  </p>
                )}
              </div>
            ) : error ? (
              <div className="text-center py-10">
                <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-500 text-lg mb-4">{error}</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Si le problème persiste, contactez-nous à contact@ledlr.org
                </p>
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Réessayer
                </Button>
              </div>
            ) : (
              <div className="text-center py-10">
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold mb-2">Paiement réussi !</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  Votre inscription à la compétition de golf est confirmée.
                </p>
                <p className="text-muted-foreground mb-2">
                  📅 12 Juin 2026 — Paris International Golf (PIG)
                </p>
                <p className="text-muted-foreground">
                  Un email de confirmation et votre CERFA fiscal vous seront envoyés prochainement.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
